import axios from "axios";
import { API } from "./auth";
import { openPdfPreview, isPdfFilename } from "./pdfPreview";

// Re-export API so components can build absolute URLs for `<img src>` /
// `window.open()` file previews without duplicating the base URL.
export { API };

/** iter164: Blob PDF ise (Content-Type application/pdf VEYA filename .pdf ile
 * bitiyorsa) doğrudan indirmek yerine global PDF Preview dialog'unu açar.
 * Non-PDF (xlsx, csv, ...) davranışı DEĞİŞMEZ — anchor click ile indirilir. */
function _tryPreviewOrDownload(blob, filename) {
  const isPdf = (blob && blob.type === "application/pdf") || isPdfFilename(filename);
  if (isPdf) {
    openPdfPreview(blob, filename);
    return;
  }
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
}

export const api = {
  // iter227 — Accept optional 3rd arg (axios config) so callers can pass an
  // AbortController signal.  Backward compatible: 3rd arg is optional.
  get: (p, params, config) => axios.get(`${API}${p}`, { params, ...(config || {}) }).then((r) => r.data),
  post: (p, body) => axios.post(`${API}${p}`, body).then((r) => r.data),
  postForm: (p, form) => axios.post(`${API}${p}`, form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  put: (p, body) => axios.put(`${API}${p}`, body).then((r) => r.data),
  patch: (p, body) => axios.patch(`${API}${p}`, body).then((r) => r.data),
  /** iter114/iter115: DELETE accepts optional body payload — FastAPI Body(...)
   * kullanan silme endpoint'lerine (belge silme gerekçesi vb.) request body
   * geçebilmek için axios `data:` opsiyonu kullanılır. */
  del: (p, body) => axios.delete(`${API}${p}`, body !== undefined ? { data: body } : undefined).then((r) => r.data),
  delete: (p, body) => axios.delete(`${API}${p}`, body !== undefined ? { data: body } : undefined).then((r) => r.data),
  getBlob: async (p) => {
    const r = await axios.get(`${API}${p}`, { responseType: "blob" });
    return r.data;
  },
  /** iter164: PDF ise Preview dialog aç, Excel/CSV/vs. direkt indir. */
  download: async (p, filename) => {
    const r = await axios.get(`${API}${p}`, { responseType: "blob" });
    _tryPreviewOrDownload(r.data, filename);
  },
  /** iter114: Authenticated file open (preview) — uses `axios.defaults`
   * Bearer token (set in lib/auth.jsx), receives blob, opens as object URL
   * in a new tab.  Fixes "Kimlik doğrulama gerekli" on <a href> / window.open. */
  openBlob: async (p) => {
    const r = await axios.get(`${API}${p}`, { responseType: "blob" });
    const blob = r.data;
    // Respect server-provided filename for the temporary URL if any.
    const url = window.URL.createObjectURL(blob);
    const win = window.open(url, "_blank", "noopener,noreferrer");
    // Fallback: if popup blocked, force navigation
    if (!win) window.location.href = url;
    // Revoke after enough time for the tab to load
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    return { url, mime: blob.type };
  },
  /** iter114 + iter164: Authenticated download — Content-Disposition filename
   * parse edilir. PDF ise Preview dialog açılır. */
  downloadFile: async (p, fallbackName = "document") => {
    const r = await axios.get(`${API}${p}`, { responseType: "blob" });
    const disp = r.headers?.["content-disposition"] || r.headers?.["Content-Disposition"] || "";
    const m = disp.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const name = m ? decodeURIComponent(m[1]) : fallbackName;
    _tryPreviewOrDownload(r.data, name);
  },
};

export const STATUS_COLORS = {
  ok: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  attention: "bg-amber-50 text-amber-700 ring-amber-600/20",
  critical: "bg-orange-50 text-orange-700 ring-orange-600/20",
  missing: "bg-red-50 text-red-700 ring-red-600/20",
  inactive: "bg-slate-100 text-slate-600 ring-slate-500/20",
};

export const SO_STATUS_LABEL = {
  draft: "Taslak",
  awaiting_approval: "Onay Bekliyor",
  approved: "Onaylandı",
  checking_inventory: "Stok Kontrolü",
  waiting_materials: "Malzeme Bekliyor",
  planning: "Planlanıyor",
  in_production: "Üretimde",
  filling: "Dolum",
  awaiting_qc: "QC Bekliyor",
  partial: "Kısmi Tamamlandı",
  completed: "Tamamlandı",
  partial_shipped: "Kısmi Sevk",
  shipped: "Sevk Edildi",
  delivered: "Teslim Edildi",
  cancelled: "İptal",
  delayed: "Gecikmiş",
};

export const PROD_STATUS_LABEL = {
  draft: "Taslak",
  waiting_materials: "Malzeme Bekliyor",
  ready: "Hazır",
  weighing: "Tartım",
  in_production: "Üretimde",
  awaiting_qc_bulk: "Bulk QC Bekliyor",
  ready_for_filling: "Dolum Hazır",
  filling: "Dolum",
  packaging: "Ambalaj",
  awaiting_qc_finished: "Bitmiş Ürün QC",
  completed: "Tamamlandı",
  blocked: "Blokeli",
  cancelled: "İptal",
};

export const CATEGORY_LABEL = {
  raw_material: "Ham Madde",
  active: "Aktif Bileşen",
  fragrance: "Parfüm",
  aux: "Yardımcı Madde",
  bulk: "Bulk",
  bottle: "Şişe",
  jar: "Kavanoz",
  cap: "Kapak",
  pump: "Pompa",
  dropper: "Damlalık",
  plug: "Tıpa",
  label: "Etiket",
  product_carton: "Ürün Kartonu",
  shipping_carton: "Sevkiyat Kartonu",
  separator: "Ayırıcı",
  shrink_wrap: "Shrink",
  film: "Film",
  seal: "Mühür",
  other_packaging: "Diğer Ambalaj",
  consumable: "Sarf",
  finished: "Bitmiş Ürün",
};
